// src/App.js
import React, { useEffect, useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import {
  auth,
  // Keep existing imports:
  GoogleAuthProvider,
  signInWithRedirect,
  signInWithPopup,
  getRedirectResult,
  onAuthStateChanged,
  signOut,
  db,
  signInWithGoogleCalendar,
  setupAuthStateListener,
  calendarService
} from './firebase/config.js'; 
import { collection, query, where, getDocs, doc, updateDoc, onSnapshot } from 'firebase/firestore';
import UserSettings from './pages/UserSettings';

// Import page and component files
import LoginPage from './pages/LoginPage.js';
import LoadingScreen from './components/LoadingScreen.js';
import Home from './pages/home.js';
import RequestSub from './pages/requestsub.js';
import AvailableSignUps from './pages/availablesignups.js';
import AcceptedJobDetails from './pages/AcceptedJobDetails.js';
import Analytics from './pages/analytics.js';
import Reports from './pages/Reports.js';
import Admin from './pages/admin.js';
import NotAuthorized from './pages/notauthorized.js';
import Sidebar from './components/sidebar.js';
import SubbingStatus from './pages/subbingstatus.js';
import MyRoster from './pages/MyRoster.js';
import RequestAbsence from './pages/RequestAbsence.js';
import CreateAbsenceOnBehalf from './pages/CreateAbsenceOnBehalf.js';
import SupervisorDashboard from './pages/SupervisorDashboard.js'; 
import SubPay from './pages/SubPay.js';
import SuperAdminPage from './pages/SuperAdminPage.js';
import TenantOnboardingWizard from './components/admin/TenantOnboardingWizard.jsx';
import TenantSetupPage from './pages/TenantSetupPage';
import SystemSetupPage from './components/admin/SystemSetupPage';
import VolunteerSetupPage from './components/admin/VolunteerSetupPage.jsx';
import TenantConfigService from './services/TenantConfigService';
import AbsenceHistory from './components/AbsenceHistory';
import DynamicVolunteerRegistration from './components/DynamicVolunteerRegistration';
import AdminDashboard from './pages/AdminDashboard.js';
import SchedulePatternUploader from './components/admin/SchedulePatternUploader';
import DivisionUploader from './components/admin/DivisionUploader';
import TimeBlocksUploader from './components/admin/TimeBlocksUploader';
import CalendarPatternsUploader from './components/admin/CalendarPatternsUploader';

// NEW: SMS Opt-In Modal Import
import SmsOptInModal from './components/SmsOptInModal';

// VOLUNTEER MANAGEMENT IMPORTS
import VolunteerManagerDashboard from './pages/VolunteerManagerDashboard';
import VolunteerApprovals from './pages/VolunteerApprovals';
import VolunteerEvents from './pages/VolunteerEvents';
import VolunteerDirectory from './pages/VolunteerDirectory';
import VolunteerRegistration from './pages/VolunteerRegistration';
import AvailableVolunteerEvents from './pages/AvailableVolunteerEvents';
import VolunteerSettings from './pages/VolunteerSettings';
import Communications from './pages/Communications';
import EventManager from './pages/EventManager';

// SUBSTITUTE MANAGEMENT IMPORTS
import SubstituteManagerDashboard from './pages/SubstituteManagerDashboard';
import SubstituteApprovals from './pages/SubstituteApprovals';
import SubstituteDirectory from './pages/SubstituteDirectory';
import SubstituteRegistration from './pages/SubstituteRegistration';
import SubstituteRegistrationSetup from './components/SubstituteRegistrationSetup';
import DynamicSubstituteRegistration from './components/DynamicSubstituteRegistration';

// FORM SETUP IMPORTS
import VolunteerRegistrationSetup from './components/VolunteerRegistrationSetup.jsx';

// NEW: Helper function to determine assigned roles based on user data
const getAssignedRoles = (supplementaryData) => {
  const roles = [];
  
  // Always include the primary role
  if (supplementaryData.role) {
    roles.push(supplementaryData.role.toLowerCase());
  }
  
  // Add secondary roles based on data or specific logic
  // Example: If someone has multiple role fields or flags
  if (supplementaryData.secondaryRoles && Array.isArray(supplementaryData.secondaryRoles)) {
    supplementaryData.secondaryRoles.forEach(role => {
      const normalizedRole = role.toLowerCase();
      if (!roles.includes(normalizedRole)) {
        roles.push(normalizedRole);
      }
    });
  }
  
  // Example: Add HR role if they have hrAccess flag
  if (supplementaryData.hrAccess && !roles.includes('hr')) {
    roles.push('hr');
  }
  
  // Example: Add Faculty/Staff if they have facultyAccess flag
  if (supplementaryData.facultyAccess && !roles.includes('faculty/staff')) {
    roles.push('faculty/staff');
  }
  
  // Fallback: if no roles found, use the primary role
  if (roles.length === 0 && supplementaryData.role) {
    roles.push(supplementaryData.role.toLowerCase());
  }
  
  console.log('🔄 Assigned roles determined:', roles);
  return roles;
};

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [facultyProfile, setFacultyProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [tenantConfig, setTenantConfig] = useState(null);
  
  // NEW: SMS Opt-In Modal State
  const [showSmsModal, setShowSmsModal] = useState(false);

  // NEW: Role switching handler
  const handleRoleChange = useCallback((newRole) => {
    console.log('🔄 Role switching to:', newRole);
    
    if (currentUser && currentUser.assignedRoles?.includes(newRole)) {
      setCurrentUser(prevUser => ({
        ...prevUser,
        activeRole: newRole
      }));
      
      // Optional: Save the preference to localStorage for persistence
      try {
        localStorage.setItem(`preferredRole_${currentUser.uid}`, newRole);
      } catch (e) {
        console.warn('Could not save role preference to localStorage:', e);
      }
      
      console.log('✅ Role switched successfully to:', newRole);
    } else {
      console.error('❌ User not authorized for role:', newRole);
    }
  }, [currentUser]);

  useEffect(() => {
    // Debug browser storage capabilities
    console.log("🔥🍪 Browser storage debug:", {
      localStorage: typeof(Storage) !== "undefined" && localStorage,
      sessionStorage: typeof(Storage) !== "undefined" && sessionStorage,
      cookiesEnabled: navigator.cookieEnabled,
      thirdPartyCookies: "unknown" // Can't easily test this
    });
    
    // Debug Firebase Auth configuration
    console.log("🔥🔧 Firebase Auth config:", {
      authDomain: auth.app.options.authDomain,
      projectId: auth.app.options.projectId,
      currentUser: auth.currentUser
    });
    
    const handleRedirect = async () => {
      try {
        console.log("🔥🔄 Checking for redirect result...");
        const result = await getRedirectResult(auth);
        if (result) {
          console.log("🔥🔄 Redirect result found:", result.user);
        } else {
          console.log("🔥🔄 No redirect result");
        }
      } catch (error) {
        console.error("🔥🔄 Error from redirect:", error);
        setAuthError(`Sign-in Error: ${error.message}`);
      }
    };
    handleRedirect();
  }, []);

  // NEW: Fetch tenant configuration when user changes
  useEffect(() => {
    if (currentUser?.tenantId) {
      const fetchTenantConfig = async () => {
        try {
          console.log('🔥🔧 Fetching tenant config for:', currentUser.tenantId);
          
          // Fetch the modules config specifically for sidebar filtering
          const moduleConfig = await TenantConfigService.getConfig(
            currentUser.tenantId, 
            'modules', 
            { 
              // Default fallback if no config exists yet
              enabledModules: {
                absenceManagement: true,
                substituteManagement: true,
                subPayManagement: false,
                volunteerManagement: false
              }
            }
          );
          
          console.log('🔥🔧 Fetched tenant config for sidebar:', moduleConfig);
          setTenantConfig(moduleConfig);
        } catch (error) {
          console.error('🔥🔧 Error fetching tenant config:', error);
          // Set defaults if fetch fails
          setTenantConfig({
            enabledModules: {
              absenceManagement: true,
              substituteManagement: true,
              subPayManagement: false,
              volunteerManagement: false
            }
          });
        }
      };
      
      fetchTenantConfig();
    } else {
      // Clear tenant config if no user or tenantId
      setTenantConfig(null);
    }
  }, [currentUser?.tenantId]);

  // NEW: Listen for role changes in real-time
  useEffect(() => {
    if (!currentUser?.firestoreDocId) return;

    console.log('🔥👂 Setting up role change listener for:', currentUser.firestoreDocId);

    const unsubscribe = onSnapshot(
      doc(db, 'faculty_staff', currentUser.firestoreDocId),
      async (docSnapshot) => {
        if (!docSnapshot.exists()) {
          console.log('🔥👂 Faculty document not found');
          return;
        }

        const facultyData = docSnapshot.data();
        console.log('🔥👂 Faculty document updated:', facultyData.role);

        // Check if the role in Firestore is different from current user's role
        if (facultyData.role && facultyData.role !== currentUser.role) {
          console.log('🔥🔄 Role change detected!', {
            old: currentUser.role,
            new: facultyData.role
          });

          try {
            // Force token refresh to get new custom claims
            const user = auth.currentUser;
            if (user) {
              console.log('🔥🔄 Forcing token refresh...');
              await user.getIdToken(true);
              
              // Get the updated token with new claims
              const tokenResult = await user.getIdTokenResult();
              console.log('🔥✅ Token refreshed with new role:', tokenResult.claims.role);

              // Show notification and reload
              alert(`Your role has been updated to: ${tokenResult.claims.role}\n\nThe page will refresh to apply changes.`);
              window.location.reload();
            }
          } catch (error) {
            console.error('🔥❌ Error refreshing token:', error);
          }
        }
      },
      (error) => {
        console.error('🔥❌ Error listening to faculty_staff changes:', error);
      }
    );

    // Cleanup listener on unmount or when currentUser changes
    return () => {
      console.log('🔥👂 Cleaning up role change listener');
      unsubscribe();
    };
  }, [currentUser?.firestoreDocId, currentUser?.role]);

  // NEW: Check if user needs to see SMS opt-in modal
  useEffect(() => {
    if (currentUser && currentUser.mobileNumber && currentUser.smsOptIn === undefined) {
      console.log('📱 User needs to see SMS opt-in modal');
      setShowSmsModal(true);
    } else {
      setShowSmsModal(false);
    }
  }, [currentUser]);

  // In App.js, replace the fetchSupplementaryUserData function with this updated version:

// Replace the fetchSupplementaryUserData function in App.js with this version
// This checks BOTH collections to detect dual-role users (e.g., teacher who is also a volunteer)

const fetchSupplementaryUserData = useCallback(async (userAuth, userTenantId = null) => {
  console.log("🔥📊 fetchSupplementaryUserData called with:", {
    userEmail: userAuth?.email,
    userTenantId,
    hasUserAuth: !!userAuth
  });
  
  if (!userAuth || !userAuth.email) {
    console.log("🔥📊 No userAuth or email - returning empty result");
    return { department: null, phone: null, title: null, supervisorId: null, foundInFaculty: false };
  }
  
  try {
    const email = userAuth.email.toLowerCase();
    let primaryUserData = null;
    let hasVolunteerAccess = false;
    let volunteerData = null;
    
    // FIRST: Check faculty_staff collection
    const facultyRef = collection(db, "faculty_staff");
    console.log("🔥📊 Checking faculty_staff collection");
    
    let facultyQuery;
    if (userTenantId) {
      console.log("🔥📊 Querying faculty_staff with tenantId:", userTenantId);
      facultyQuery = query(facultyRef, 
        where("email", "==", email),
        where("tenantId", "==", userTenantId) 
      );
    } else {
      console.log("🔥📊 Querying faculty_staff by email only:", email);
      facultyQuery = query(facultyRef, 
        where("email", "==", email)
      );
    }
    
    console.log("🔥📊 Executing faculty_staff query...");
    const facultySnapshot = await getDocs(facultyQuery);
    console.log("🔥📊 Faculty_staff query completed. Found", facultySnapshot.docs.length, "documents");

    if (!facultySnapshot.empty) {
      // Found in faculty_staff
      const userDoc = facultySnapshot.docs[0];
      const userDocData = userDoc.data();
      console.log("🔥📊 ✅ Found user in faculty_staff!");
      
      primaryUserData = { 
        docId: userDoc.id,
        ...userDocData,
        foundInFaculty: true,
        uidInFirestore: !!userDocData.uid,
        userType: 'staff'
      };
    }

    // ALWAYS check volunteers collection to see if they're ALSO a volunteer
    console.log("🔥📊 Checking if user is also a volunteer...");
    const volunteersRef = collection(db, "volunteers");
    
    let volunteerQuery;
    if (userTenantId || (primaryUserData && primaryUserData.tenantId)) {
      const tenantToCheck = userTenantId || primaryUserData.tenantId;
      console.log("🔥📊 Querying volunteers with tenantId:", tenantToCheck);
      volunteerQuery = query(volunteersRef, 
        where("email", "==", email),
        where("tenantId", "==", tenantToCheck),
        where("status", "==", "approved")
      );
    } else {
      console.log("🔥📊 Querying volunteers by email only:", email);
      volunteerQuery = query(volunteersRef, 
        where("email", "==", email),
        where("status", "==", "approved")
      );
    }
    
    console.log("🔥📊 Executing volunteers query...");
    const volunteerSnapshot = await getDocs(volunteerQuery);
    console.log("🔥📊 Volunteers query completed. Found", volunteerSnapshot.docs.length, "documents");

    if (!volunteerSnapshot.empty) {
      hasVolunteerAccess = true;
      const volunteerDoc = volunteerSnapshot.docs[0];
      volunteerData = volunteerDoc.data();
      console.log("🔥📊 ✅ User is also a volunteer!");
      
      // If user wasn't found in faculty_staff, use volunteer as primary
      if (!primaryUserData) {
        console.log("🔥📊 Using volunteer as primary authentication");
        
        primaryUserData = {
          docId: volunteerDoc.id,
          ...volunteerData,
          // Map volunteer data to expected structure
          role: 'Volunteer',
          department: volunteerData.skills?.join(', ') || 'Volunteer',
          displayName: volunteerData.firstName + ' ' + volunteerData.lastName,
          foundInFaculty: true, // Set to true so auth succeeds
          uidInFirestore: !!volunteerData.uid,
          userType: 'volunteer',
          // Volunteer-specific fields
          isVolunteer: true,
          hasElevatedPrivileges: false,
          isSupervisor: false
        };
      }
    }
    
    // If we found the user in either collection, return the combined result
      if (primaryUserData) {
        // Check if this user is anyone's supervisor
        let isSupervisor = false;
        try {
          const supervisorCheckQuery = query(
            collection(db, "faculty_staff"),
            where("supervisorId", "==", primaryUserData.docId),
            where("tenantId", "==", primaryUserData.tenantId)
          );
          const supervisorSnapshot = await getDocs(supervisorCheckQuery);
          isSupervisor = !supervisorSnapshot.empty;
          console.log("🔥📊 Supervisor check:", {
            docId: primaryUserData.docId,
            isSupervisor,
            directReportsCount: supervisorSnapshot.docs.length
          });
        } catch (supervisorError) {
          console.error("🔥📊 Error checking supervisor status:", supervisorError);
        }

        const result = {
          ...primaryUserData,
          // Add volunteer access flags if they're also a volunteer
          isAlsoVolunteer: hasVolunteerAccess,
          hasVolunteerAccess: hasVolunteerAccess,
          volunteerProfile: hasVolunteerAccess ? volunteerData : null,
          // Add computed supervisor status
          isSupervisor: isSupervisor
        };
      
      console.log("🔥📊 Returning combined result:", {
        email: result.email,
        primaryRole: result.role,
        isAlsoVolunteer: result.isAlsoVolunteer,
        hasVolunteerAccess: result.hasVolunteerAccess,
        userType: result.userType
      });
      
      return result;
    }

    // Not found in either collection
    console.warn("🔥📊 ❌ User not found in faculty_staff or volunteers for email:", email);
    return { department: null, phone: null, title: null, supervisorId: null, foundInFaculty: false };
    
  } catch (error) {
    console.error("🔥📊 💥 Error in fetchSupplementaryUserData:", error);
    return { department: null, phone: null, title: null, supervisorId: null, foundInFaculty: false, error: true };
  }
}, []);

  // UPDATED: Use basic auth state listener for debugging (temporarily disable calendar)
  useEffect(() => {
    console.log("🔥📅 Setting up auth state listener (debug mode)...");
    
    // TEMPORARY: Use basic onAuthStateChanged instead of enhanced version
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log("🔥📅 Auth state change:", firebaseUser ? "USER DETECTED" : "NO USER");
      
      if (firebaseUser) {
        try {
          setAuthError(null);
          setLoading(true);
          
          console.log("🔥📅 Processing user with basic auth...");
          console.log("🔥📅 Firebase user:", {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName
          });
          
          // ⭐ CRITICAL FIX: Force token refresh to get custom claims
          console.log("🔄 Refreshing token to get custom claims...");
          try {
            await firebaseUser.getIdToken(true); // Force refresh
            
            // Verify the claims were loaded
            const idTokenResult = await firebaseUser.getIdTokenResult();
            console.log("✅ Custom claims loaded:", idTokenResult.claims);
            
            // Check if we have the required claims
            if (!idTokenResult.claims.role || !idTokenResult.claims.tenantId) {
              console.warn("⚠️ Custom claims not set yet. Waiting for Cloud Function...");
              // Optional: Add a small delay and retry once
              await new Promise(resolve => setTimeout(resolve, 2000));
              await firebaseUser.getIdToken(true);
              const retryTokenResult = await firebaseUser.getIdTokenResult();
              console.log("🔄 Retry - Custom claims:", retryTokenResult.claims);
            }
          } catch (tokenError) {
            console.error("❌ Error refreshing token:", tokenError);
          }
          
          // Get supplementary data (your existing logic)
          // Get supplementary data (your existing logic)
const supplementaryData = await fetchSupplementaryUserData(
  firebaseUser, 
  null // Don't filter by tenantId for debugging
);

console.log("🔥📅 Supplementary data result:", supplementaryData);

if (supplementaryData && supplementaryData.foundInFaculty) {
  console.log("🔥📅 User found in faculty_staff or volunteers");
  
  // ⭐ CRITICAL: Get token claims to extract tenantIds
  const idTokenResult = await firebaseUser.getIdTokenResult();
  console.log("🔥 Token claims:", idTokenResult.claims);
  
  // NEW: Determine assigned roles
  const assignedRoles = getAssignedRoles(supplementaryData);
  
  // NEW: Get preferred role from localStorage or use primary role
  let preferredRole = supplementaryData.role?.toLowerCase();
  try {
    const savedRole = localStorage.getItem(`preferredRole_${firebaseUser.uid}`);
    if (savedRole && assignedRoles.includes(savedRole)) {
      preferredRole = savedRole;
    }
  } catch (e) {
    console.warn('Could not read role preference from localStorage:', e);
  }
  
  // Create basic user object with role switching support
  const finalUser = {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName || supplementaryData.displayName || supplementaryData.name,
    ...supplementaryData,
    
    // ⭐ CRITICAL FIX: Add tenantIds from custom claims
    tenantId: idTokenResult.claims.tenantId || supplementaryData.tenantId,
    tenantIds: idTokenResult.claims.tenantIds || [supplementaryData.tenantId], // Multi-tenant support
    
    // NEW: Role switching properties
    assignedRoles: assignedRoles,
    activeRole: preferredRole,
    
    // Calendar and role permissions
    hasCalendarPermissions: true,
    substituteType: idTokenResult.claims.substituteType, // Add this too
    isSystemAdmin: supplementaryData.role === 'System Administrator',
    isEducationalAdmin: supplementaryData.role === 'Educational Administrator',
    isOutsideSub: supplementaryData.role === 'OutsideSub',
    hasElevatedPrivileges: supplementaryData.role === 'System Administrator' || supplementaryData.role === 'Educational Administrator',
    isSupervisor: supplementaryData.isSupervisor || false,
    isSuperAdmin: supplementaryData.role === 'Super Admin'
  };
  
  console.log("🔥📅 Final user object with tenantIds:", finalUser);
  console.log("🏫 User tenantIds:", finalUser.tenantIds);
  console.log("🔄 User has", assignedRoles.length, "assigned roles:", assignedRoles);
  console.log("🔄 Active role:", preferredRole);
            
            setCurrentUser(finalUser);
            setFacultyProfile(supplementaryData);
            setLoading(false);
            
          } else {
            console.error("🔥📅 User not found in faculty_staff or volunteers");
            setAuthError("Your account is not set up in the system. Please contact your administrator to add you to the faculty/staff or volunteer directory.");
            await signOut(auth);
            setCurrentUser(null);
            setFacultyProfile(null);
            setLoading(false);
          }
          
        } catch (error) {
          console.error("🔥📅 Error processing user:", error);
          setAuthError(error.message);
          await signOut(auth);
          setCurrentUser(null);
          setFacultyProfile(null);
          setLoading(false);
        }
      } else {
        console.log("🔥📅 No user - clearing state");
        setCurrentUser(null);
        setFacultyProfile(null);
        setLoading(false);
      }
    });
    
    return () => unsubscribe();
  }, [fetchSupplementaryUserData]);

  // UPDATED: Simplified Google Sign-In for debugging
  const handleGoogleSignIn = async () => {
  try {
    setAuthError(null);
    console.log("🔥🔑 Attempting Google sign-in with calendar permissions...");
    
    // Use enhanced provider with calendar scopes
    const provider = new GoogleAuthProvider();
    
    // Add calendar scopes
    provider.addScope('https://www.googleapis.com/auth/userinfo.email');
    provider.addScope('https://www.googleapis.com/auth/userinfo.profile');
    provider.addScope('https://www.googleapis.com/auth/calendar');
    provider.addScope('https://www.googleapis.com/auth/calendar.events');
    
    console.log("🔥🔑 Trying popup sign-in with calendar scopes...");
    
    try {
      // Try popup first
      const result = await signInWithPopup(auth, provider);
      console.log("✅ Google sign-in successful with calendar scopes:", result.user);
      console.log("✅ User email:", result.user.email);
      return;
    } catch (popupError) {
      console.log("🔥🔑 Popup failed, trying redirect...", popupError);
      
      if (popupError.code === 'auth/popup-blocked' || 
          popupError.code === 'auth/cancelled-popup-request' ||
          popupError.message.includes('popup')) {
        console.log("🔥🔑 Attempting redirect sign-in...");
        await signInWithRedirect(auth, provider);
        return;
      } else if (popupError.code !== 'auth/popup-closed-by-user') {
        throw popupError;
      } else {
        throw popupError;
      }
    }
    
  } catch (error) {
    console.error("🔥🔑 Sign-in failed:", error);
    setAuthError(`Sign-in Error: ${error.message}`);
  }
};

  const handleSignOut = async () => {
     try {
       await signOut(auth);
       // Clear calendar service state on sign out (if available)
       if (calendarService && calendarService.setCurrentUser) {
         calendarService.setCurrentUser(null);
       }
     } catch (error) {
       console.error("App.js: Sign out error", error);
       setAuthError("Error signing out. Please try again.");
     }
  };

  // Helper function to check if user is a volunteer
  const isVolunteer = () => {
    return currentUser.role === 'Volunteer' || 
           currentUser.userType === 'volunteer' || 
           currentUser.isVolunteer === true;
  };

  // Helper function to check if user can manage volunteers
  const canManageVolunteers = () => {
    return currentUser.hasElevatedPrivileges || 
           currentUser.role === 'Volunteer Manager' || 
           currentUser.activeRole === 'volunteer manager' ||
           currentUser.isSuperAdmin;
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
  <Router>
    {!currentUser ? (
            <Routes>
              {/* Public Registration Routes (no auth required) */}
              <Route path="/volunteer-register" element={<DynamicVolunteerRegistration />} />
              <Route path="/substitute-register" element={<DynamicSubstituteRegistration />} />
              <Route path="*" element={<LoginPage handleLogin={handleGoogleSignIn} authError={authError} />} />
            </Routes>
              ) : (
                <div style={{ display: 'flex' }}>
                  {/* UPDATED: Add role switching handler and tenantConfig to Sidebar */}
                  <Sidebar 
                    currentUser={currentUser} 
                    facultyProfile={facultyProfile} 
                    handleSignOut={handleSignOut}
                    tenantConfig={tenantConfig}
                    hasCalendarPermissions={currentUser.hasCalendarPermissions}
                    onRoleChange={handleRoleChange}
                  />
                  <div style={{ marginLeft: '260px', padding: '40px', flex: 1, backgroundColor: '#FFFFFF', minHeight: '100vh', boxSizing: 'border-box' }}>
                    
                    {/* NEW: SMS Opt-In Modal */}
                    {showSmsModal && (
                      <SmsOptInModal 
                        user={currentUser} 
                        onClose={() => setShowSmsModal(false)} 
                      />
                    )}
                    
                    <Routes>
            <Route path="/" element={<Home currentUser={currentUser} />} />
            <Route path="/unauthorized" element={<NotAuthorized />} />
            
            {/* NEW: User Settings - Available to ALL users */}
            <Route path="/user-settings" element={<UserSettings currentUser={currentUser} />} />
            
            {/* NEW: Tenant Setup Admin - Only has access to this route */}
            {(currentUser.role === 'Tenant Setup Admin' || currentUser.activeRole === 'tenant setup admin') && (
              <Route path="/tenant-setup" element={<TenantSetupPage currentUser={currentUser} />} />
            )}
            
            {/* Existing routes - Only for non-Tenant Setup Admin users */}
            {currentUser.role !== 'Tenant Setup Admin' && currentUser.activeRole !== 'tenant setup admin' && (
              <>
                {/* VOLUNTEER MANAGEMENT ROUTES */}
                <Route path="/volunteer-dashboard" element={<VolunteerManagerDashboard currentUser={currentUser} />} />
                <Route path="/volunteer-approvals" element={<VolunteerApprovals currentUser={currentUser} />} />
                <Route path="/volunteer-events" element={<VolunteerEvents currentUser={currentUser} />} />
                <Route path="/volunteer-directory" element={<VolunteerDirectory currentUser={currentUser} />} />
                <Route path="/communications" element={<Communications currentUser={currentUser} />} />
                
                {/* NEW: Volunteer Settings Route - Available to volunteers and managers */}
                {(isVolunteer() || canManageVolunteers()) && (
                  <Route path="/volunteer-settings" element={<VolunteerSettings currentUser={currentUser} />} />
                )}
                
                {/* SUBSTITUTE MANAGEMENT ROUTES */}
                <Route path="/substitute-manager-dashboard" element={<SubstituteManagerDashboard currentUser={currentUser} />} />
                <Route path="/substitute-approvals" element={<SubstituteApprovals currentUser={currentUser} />} />
                <Route path="/substitute-directory" element={<SubstituteDirectory currentUser={currentUser} />} />
                
                {/* FORM SETUP ROUTES - For admins and managers */}
                {(currentUser.hasElevatedPrivileges || 
                  currentUser.role === 'Substitute Manager' || 
                  currentUser.activeRole === 'substitute manager') && (
                  <Route 
                    path="/admin/substitute-setup" 
                    element={<SubstituteRegistrationSetup tenantId={currentUser.tenantId} />} 
                  />
                )}
                
                {(currentUser.hasElevatedPrivileges || 
                  currentUser.role === 'Volunteer Manager' || 
                  currentUser.activeRole === 'volunteer manager') && (
                  <Route 
                    path="/admin/volunteer-registration-setup" 
                    element={<VolunteerRegistrationSetup currentUser={currentUser} />} 
                  />
                )}
                
                {/* Available Volunteer Events - Available to volunteers and managers/admins */}
                <Route path="/available-volunteer-events" element={<AvailableVolunteerEvents currentUser={currentUser} />} />
                
                {/* EXISTING ROUTES */}
                {(currentUser.role === 'HR' || currentUser.activeRole === 'hr') && <Route path="/sub-pay" element={<SubPay currentUser={currentUser} />} />}
                
                {currentUser.role !== "OutsideSub" && currentUser.activeRole !== 'outside sub' && !isVolunteer() && (
                  <>
                    <Route path="/request-sub" element={<RequestSub currentUser={currentUser} />} />
                    <Route path="/subbing-status" element={<SubbingStatus currentUser={currentUser} />} />
                    <Route path="/request-absence" element={<RequestAbsence currentUser={currentUser} />} />
                    {(currentUser.isSupervisor || currentUser.hasElevatedPrivileges || currentUser.role === 'HR' || currentUser.activeRole === 'hr' || currentUser.role === 'Volunteer Manager' || currentUser.activeRole === 'volunteer manager') && (
                      <>
                        <Route path="/create-absence-on-behalf" element={<CreateAbsenceOnBehalf currentUser={currentUser} />} />
                        <Route path="/supervisor-dashboard" element={<SupervisorDashboard currentUser={currentUser} />} />
                      </>
                    )}

                    {/* Admin Dashboard - HR and Educational Administrator only */}
                    {(currentUser.hasElevatedPrivileges || currentUser.role === 'HR' || currentUser.activeRole === 'hr' || currentUser.role === 'Educational Administrator' || currentUser.activeRole === 'educational administrator') && (
                      <Route path="/admin-dashboard" element={<AdminDashboard currentUser={currentUser} />} />
                    )}
                  </>
                )}
                
                {/* Adjusted to include volunteers for available-signups */}
                {!isVolunteer() && (
                  <Route path="/available-signups" element={<AvailableSignUps currentUser={currentUser} />} />
                )}
                <Route path="/absence-history" element={<AbsenceHistory currentUser={currentUser} />} />
                <Route path="/my-roster" element={<MyRoster currentUser={currentUser} />} />
                <Route path="/accepted-job/:docId/:period" element={<AcceptedJobDetails currentUser={currentUser} />} />
                
               {/* SYSTEM ADMINISTRATOR ONLY ROUTES */}
                {currentUser.isSystemAdmin && (
                  <>
                    <Route path="/admin/system-setup" element={<SystemSetupPage currentUser={currentUser} />} />
                    <Route path="/admin/volunteer-setup" element={<VolunteerSetupPage currentUser={currentUser} />} />
                    <Route path="/admin/upload-schedule-patterns" element={<SchedulePatternUploader currentUser={currentUser} />} />
                    <Route path="/admin/upload-divisions" element={<DivisionUploader currentUser={currentUser} />} />
                    <Route path="/admin/upload-time-blocks" element={<TimeBlocksUploader currentUser={currentUser} />} />
                    <Route path="/admin/upload-calendar-patterns" element={<CalendarPatternsUploader currentUser={currentUser} />} />
                  </>
                )}
                
                {/* ELEVATED PRIVILEGES ROUTES (System Admin + Educational Admin) */}
                {currentUser.hasElevatedPrivileges && (
                  <>
                    <Route path="/analytics" element={<Analytics currentUser={currentUser} />} />
                    <Route path="/reports" element={<Reports currentUser={currentUser} />} />
                    <Route path="/admin" element={<Admin currentUser={currentUser} />} />
                  </>
                )}
                
                {currentUser.isSuperAdmin && (
                  <>
                    <Route path="/super-admin" element={<SuperAdminPage />} />
                    <Route path="/admin/onboard-school" element={<TenantOnboardingWizard />} />
                  </>
                )}
              </>
            )}
            
            {/* Redirect based on role */}
            <Route path="*" element={
              (currentUser.role === 'Tenant Setup Admin' || currentUser.activeRole === 'tenant setup admin')
                ? <Navigate to="/tenant-setup" replace />
                : <Navigate to="/" replace />
            } />
          </Routes>
        </div>
      </div>
    )}
  </Router>
);
}
export default App;